import { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardService, type TodoItem } from '../services/api/dashboardService';

export default function TodoList() {
  
  // Estados da To-Do List
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [novoTodo, setNovoTodo] = useState({
    titulo: '',
    descricao: '',
    prioridade: 'media' as 'baixa' | 'media' | 'alta',
    categoria: 'geral',
    data_vencimento: ''
  });
  const [mostrarFormTodo, setMostrarFormTodo] = useState(false);
  const [editandoTodo, setEditandoTodo] = useState<{ id: number; dados: any } | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'pendente' | 'em_andamento' | 'concluida'>('todos');
  const [filtroPrioridade, setFiltroPrioridade] = useState<'todos' | 'baixa' | 'media' | 'alta'>('todos');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todos');
  const todoListRef = useRef<HTMLUListElement>(null);

  // Função para carregar todos
  const carregarTodos = useCallback(async () => {
    try {
      setCarregando(true);
      // console.log('[TodoList] Carregando tarefas...');
      
      const todoItems = await dashboardService.listarTodos();
      // console.log('[TodoList] Tarefas carregadas:', todoItems);
      setTodos(todoItems || []);
      
    } catch (error) {
      console.error('[TodoList] Erro ao carregar tarefas:', error);
      setTodos([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  // Carregar todos ao montar
  useEffect(() => {
    carregarTodos();
  }, [carregarTodos]);

  // Filtrar todos baseado nos filtros selecionados
  const todosFiltrados = todos.filter(todo => {
    if (filtroStatus !== 'todos' && todo.status !== filtroStatus) return false;
    if (filtroPrioridade !== 'todos' && todo.prioridade !== filtroPrioridade) return false;
    if (filtroCategoria !== 'todos' && todo.categoria !== filtroCategoria) return false;
    return true;
  });

  // Obter categorias únicas
  const categorias = Array.from(new Set(todos.map(todo => todo.categoria).filter(Boolean)));

  // Funções da To-Do List
  const adicionarTodo = async () => {
    try {
      if (!novoTodo.titulo.trim()) {
        alert('Título é obrigatório');
        return;
      }

      const novoItem: TodoItem = {
        id: Date.now(), // ID temporário
        titulo: novoTodo.titulo,
        descricao: novoTodo.descricao,
        prioridade: novoTodo.prioridade,
        status: 'pendente',
        categoria: novoTodo.categoria,
        data_vencimento: novoTodo.data_vencimento || undefined,
        ordem: todos.length + 1,
        cod_empresa: 0, // Será definido pelo servidor
        usuario_id: 0 // Será definido pelo servidor
      };

      // Otimistic update - adicionar na UI imediatamente
      setTodos(prevTodos => [...prevTodos, novoItem]);
      setNovoTodo({
        titulo: '',
        descricao: '',
        prioridade: 'media',
        categoria: 'geral',
        data_vencimento: ''
      });
      setMostrarFormTodo(false);

      const resultado = await dashboardService.criarTodo({
        titulo: novoTodo.titulo,
        descricao: novoTodo.descricao,
        prioridade: novoTodo.prioridade,
        status: 'pendente',
        categoria: novoTodo.categoria,
        data_vencimento: novoTodo.data_vencimento || undefined
      });

      if (resultado.sucesso) {
        // Recarregar dados para obter ID real e sincronizar
        carregarTodos();
      } else {
        console.error('[TodoList] Erro no servidor ao criar todo:', resultado.mensagem);
        // Reverter mudança em caso de erro
        carregarTodos();
        alert(resultado.mensagem);
      }
    } catch (error) {
      console.error('[TodoList] Erro ao adicionar todo:', error);
      // Reverter mudança em caso de erro
      carregarTodos();
      alert('Erro ao adicionar item');
    }
  };

  const alterarStatusTodo = async (id: number, novoStatus: 'pendente' | 'em_andamento' | 'concluida') => {
    try {
      // console.log('[TodoList] Alterando status todo:', { id, novoStatus });
      
      // Otimistic update - atualizar UI imediatamente
      setTodos(prevTodos => 
        prevTodos.map(todo => 
          todo.id === id ? { ...todo, status: novoStatus } : todo
        )
      );
      
      const resultado = await dashboardService.atualizarTodo(id, { status: novoStatus });
      if (!resultado.sucesso) {
        console.error('[TodoList] Erro no servidor ao alterar status:', resultado.mensagem);
        // Reverter mudança em caso de erro
        carregarTodos();
        alert(resultado.mensagem);
      }
    } catch (error) {
      console.error('[TodoList] Erro ao alterar status:', error);
      // Reverter mudança em caso de erro
      carregarTodos();
      alert('Erro ao alterar status');
    }
  };

  const excluirTodo = async (id: number) => {
    try {
      // console.log('[TodoList] Excluindo todo:', id);
      
      // Otimistic update - remover da UI imediatamente
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== id));
      
      const resultado = await dashboardService.excluirTodo(id);
      if (!resultado.sucesso) {
        console.error('[TodoList] Erro no servidor ao excluir:', resultado.mensagem);
        // Reverter mudança em caso de erro
        carregarTodos();
        alert(resultado.mensagem);
      }
    } catch (error) {
      console.error('[TodoList] Erro ao excluir todo:', error);
      // Reverter mudança em caso de erro
      carregarTodos();
      alert('Erro ao excluir item');
    }
  };

  // Função para iniciar edição
  const iniciarEdicaoTodo = (todo: TodoItem) => {
    setEditandoTodo({
      id: todo.id!,
      dados: {
        titulo: todo.titulo,
        descricao: todo.descricao || '',
        prioridade: todo.prioridade,
        categoria: todo.categoria || 'geral',
        data_vencimento: todo.data_vencimento || ''
      }
    });
  };

  // Função para salvar edição
  const salvarEdicaoTodo = async () => {
    try {
      if (!editandoTodo || !editandoTodo.dados.titulo.trim()) {
        alert('Título é obrigatório');
        return;
      }

      // console.log('[TodoList] Salvando edição todo:', editandoTodo);
      
      // Otimistic update - atualizar UI imediatamente
      setTodos(prevTodos => 
        prevTodos.map(todo => 
          todo.id === editandoTodo.id 
            ? { ...todo, ...editandoTodo.dados }
            : todo
        )
      );
      setEditandoTodo(null);

      const resultado = await dashboardService.atualizarTodo(editandoTodo.id, editandoTodo.dados);
      if (!resultado.sucesso) {
        console.error('[TodoList] Erro no servidor ao atualizar:', resultado.mensagem);
        // Reverter mudança em caso de erro
        carregarTodos();
        alert(resultado.mensagem);
      }
    } catch (error) {
      console.error('[TodoList] Erro ao atualizar todo:', error);
      // Reverter mudança em caso de erro
      carregarTodos();
      alert('Erro ao atualizar item');
    }
  };

  // Função para cancelar edição
  const cancelarEdicaoTodo = () => {
    setEditandoTodo(null);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltroStatus('todos');
    setFiltroPrioridade('todos');
    setFiltroCategoria('todos');
  };

  // Inicializar sortable quando todos carregarem
  useEffect(() => {
    if (todoListRef.current && todos.length > 0) {
      // console.log('[TodoList] Inicializando SortableJS para to-do list');
      
      // Cleanup function segura
      const cleanupSortable = () => {
        try {
          if (todoListRef.current && (todoListRef.current as any).sortableInstance) {
            // console.log('[TodoList] Destruindo instância anterior do SortableJS');
            (todoListRef.current as any).sortableInstance.destroy();
            (todoListRef.current as any).sortableInstance = null;
          }
        } catch (error) {
          // console.warn('[TodoList] Erro ao destruir sortable:', error);
          if (todoListRef.current) {
            (todoListRef.current as any).sortableInstance = null;
          }
        }
      };

      // Limpar instância anterior
      cleanupSortable();
      
      // Carregar SortableJS dinamicamente
      const loadSortableJS = async () => {
        try {
          if (!(window as any).Sortable) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/sortablejs@1.15.0/Sortable.min.js';
            script.crossOrigin = 'anonymous';
            
            await new Promise((resolve, reject) => {
              script.onload = resolve;
              script.onerror = reject;
              document.head.appendChild(script);
            });
          }

          if (!todoListRef.current) {
            // console.warn('[TodoList] TodoListRef não existe mais');
            return;
          }
          
          // Inicializar SortableJS seguindo padrão AdminLTE
          const sortableInstance = new (window as any).Sortable(todoListRef.current, {
            group: 'shared',
            handle: '.handle',
            ghostClass: 'sort-highlight',
            animation: 150,
            disabled: !!editandoTodo, // Desabilitar durante edição
            onEnd: async (evt: any) => {
              const itemId = parseInt(evt.item.dataset.id || '0');
              const newIndex = evt.newIndex;
              const oldIndex = evt.oldIndex;
              
              // console.log('[TodoList] Item reordenado via SortableJS:', { 
                // itemId, newIndex, oldIndex 
              // });
              
              if (newIndex === oldIndex) return; // Não houve mudança real
              
              try {
                // Recalcular ordens baseado na nova posição
                const todosOrdenados = Array.from(todoListRef.current!.children)
                  .map((li, index) => {
                    const idStr = (li as HTMLElement).dataset.id;
                    const id = parseInt(idStr || '0');
                    return {
                      id: id,
                      ordem: index + 1
                    };
                  })
                  .filter(item => !isNaN(item.id) && item.id > 0);
                  
                // console.log('[TodoList] Nova ordem (SortableJS):', todosOrdenados);
                
                if (todosOrdenados.length === 0) {
                  // console.warn('[TodoList] Nenhum item válido para reordenar');
                  return;
                }
                
                const resultado = await dashboardService.reordenarTodos(todosOrdenados);
                if (resultado.sucesso) {
                  // Atualizar estado local sem recarregar tudo
                  setTodos(prevTodos => {
                    const novaTodos = [...prevTodos];
                    novaTodos.sort((a, b) => {
                      const ordemA = todosOrdenados.find(t => t.id === a.id)?.ordem || 0;
                      const ordemB = todosOrdenados.find(t => t.id === b.id)?.ordem || 0;
                      return ordemA - ordemB;
                    });
                    return novaTodos;
                  });
                } else {
                  console.error('[TodoList] Erro ao reordenar:', resultado.mensagem);
                  carregarTodos();
                }
              } catch (error) {
                console.error('[TodoList] Erro ao reordenar todos:', error);
                carregarTodos();
              }
            }
          });
          
          // Armazenar instância para cleanup
          (todoListRef.current as any).sortableInstance = sortableInstance;
          
          // Aplicar cursor de movimento nos handles
          const handles = todoListRef.current!.querySelectorAll('.handle');
          handles.forEach((handle: any) => {
            handle.style.cursor = 'move';
          });

          // console.log('[TodoList] SortableJS inicializado com sucesso');
          
        } catch (error) {
          console.error('[TodoList] Erro ao carregar SortableJS:', error);
        }
      };
      
      loadSortableJS();
      
      return () => {
        cleanupSortable();
      };
    }
  }, [todos, editandoTodo, carregarTodos]);

  if (carregando) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Carregando...</span>
        </div>
        <p className="mt-3">Carregando lista de tarefas...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header da página */}
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                <i className="fas fa-tasks mr-2"></i>
                Lista de Tarefas
              </h1>
            </div>
            <div className="col-sm-6">
              <button 
                className="btn btn-primary float-right"
                onClick={() => setMostrarFormTodo(!mostrarFormTodo)}
              >
                <i className="fas fa-plus mr-2"></i>
                Nova Tarefa
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="row mb-3">
        <div className="col-md-12">
          <div className="card card-outline card-primary">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-filter mr-2"></i>
                Filtros
              </h3>
              <div className="card-tools">
                <button 
                  className="btn btn-tool"
                  onClick={limparFiltros}
                  title="Limpar filtros"
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3">
                  <label htmlFor="todoFiltroStatus">Status:</label>
                  <select 
                    id="todoFiltroStatus"
                    className="form-control form-control-sm"
                    value={filtroStatus}
                    onChange={(e) => setFiltroStatus(e.target.value as any)}
                  >
                    <option value="todos">Todos</option>
                    <option value="pendente">Pendentes</option>
                    <option value="em_andamento">Em Andamento</option>
                    <option value="concluida">Concluídas</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label htmlFor="todoFiltroPrioridade">Prioridade:</label>
                  <select 
                    id="todoFiltroPrioridade"
                    className="form-control form-control-sm"
                    value={filtroPrioridade}
                    onChange={(e) => setFiltroPrioridade(e.target.value as any)}
                  >
                    <option value="todos">Todas</option>
                    <option value="alta">Alta</option>
                    <option value="media">Média</option>
                    <option value="baixa">Baixa</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label htmlFor="todoFiltroCategoria">Categoria:</label>
                  <select 
                    id="todoFiltroCategoria"
                    className="form-control form-control-sm"
                    value={filtroCategoria}
                    onChange={(e) => setFiltroCategoria(e.target.value)}
                  >
                    <option value="todos">Todas</option>
                    {categorias.map(categoria => (
                      <option key={categoria} value={categoria}>{categoria}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 d-flex align-items-end">
                  <div className="text-muted small">
                    <strong>{todosFiltrados.length}</strong> de <strong>{todos.length}</strong> tarefas
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de tarefas */}
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fas fa-list mr-2"></i>
                Tarefas
              </h3>
            </div>
            <div className="card-body p-0">
              {/* Formulário para nova tarefa */}
              {mostrarFormTodo && (
                <div className="card-header bg-light">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label htmlFor="novoTodoTitulo">Título *</label>
                        <input
                          id="novoTodoTitulo"
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Título da tarefa"
                          value={novoTodo.titulo}
                          onChange={(e) => setNovoTodo({ ...novoTodo, titulo: e.target.value })}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group mb-2">
                        <label htmlFor="novoTodoCategoria">Categoria</label>
                        <input
                          id="novoTodoCategoria"
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Categoria (ex: trabalho, pessoal)"
                          value={novoTodo.categoria}
                          onChange={(e) => setNovoTodo({ ...novoTodo, categoria: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group mb-2">
                    <label htmlFor="novoTodoDescricao">Descrição</label>
                    <textarea
                      id="novoTodoDescricao"
                      className="form-control form-control-sm"
                      placeholder="Descrição detalhada (opcional)"
                      rows={2}
                      value={novoTodo.descricao}
                      onChange={(e) => setNovoTodo({ ...novoTodo, descricao: e.target.value })}
                    />
                  </div>
                  <div className="row mb-2">
                    <div className="col-md-6">
                      <label htmlFor="novoTodoPrioridade">Prioridade</label>
                      <select
                        id="novoTodoPrioridade"
                        className="form-control form-control-sm"
                        value={novoTodo.prioridade}
                        onChange={(e) => setNovoTodo({ ...novoTodo, prioridade: e.target.value as 'baixa' | 'media' | 'alta' })}
                      >
                        <option value="baixa">Baixa</option>
                        <option value="media">Média</option>
                        <option value="alta">Alta</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="novoTodoVencimento">Data de vencimento</label>
                      <input
                        id="novoTodoVencimento"
                        type="date"
                        className="form-control form-control-sm"
                        value={novoTodo.data_vencimento}
                        onChange={(e) => setNovoTodo({ ...novoTodo, data_vencimento: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="btn-group">
                    <button 
                      className="btn btn-success btn-sm"
                      onClick={adicionarTodo}
                    >
                      <i className="fas fa-save mr-1"></i>
                      Salvar
                    </button>
                    <button 
                      className="btn btn-secondary btn-sm"
                      onClick={() => setMostrarFormTodo(false)}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              )}

              {/* Lista de tarefas */}
              <ul 
                className="todo-list connectedSortable" 
                data-widget="todo-list"
                ref={todoListRef}
                style={{ minHeight: '300px' }}
              >
                {todosFiltrados.map((todo, index) => (
                  <li 
                    key={todo.id} 
                    className={`${todo.status === 'concluida' ? 'done' : ''}`}
                    data-id={todo.id}
                    data-ordem={todo.ordem || index + 1}
                    style={{ 
                      position: 'relative', 
                      minHeight: editandoTodo && editandoTodo.id === todo.id ? 'auto' : '60px',
                      padding: '15px 20px'
                    }}
                  >
                    {editandoTodo && editandoTodo.id === todo.id ? (
                      // Modo de edição
                      <div className="todo-edit-form p-3" style={{ 
                        backgroundColor: '#f8f9fa', 
                        border: '1px solid #dee2e6', 
                        borderRadius: '0.25rem',
                        width: '100%',
                        boxSizing: 'border-box'
                      }}>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label className="form-label text-sm font-weight-bold" htmlFor={`todoEditTitulo-${todo.id}`}>Título:</label>
                              <input
                                id={`todoEditTitulo-${todo.id}`}
                                type="text"
                                className="form-control form-control-sm"
                                value={editandoTodo.dados.titulo}
                                onChange={(e) => setEditandoTodo({
                                  ...editandoTodo,
                                  dados: { ...editandoTodo.dados, titulo: e.target.value }
                                })}
                                placeholder="Título da tarefa"
                                autoFocus
                              />
                            </div>
                          </div>
                          <div className="col-md-6">
                            <div className="form-group mb-2">
                              <label className="form-label text-sm font-weight-bold" htmlFor={`todoEditCategoria-${todo.id}`}>Categoria:</label>
                              <input
                                id={`todoEditCategoria-${todo.id}`}
                                type="text"
                                className="form-control form-control-sm"
                                value={editandoTodo.dados.categoria || 'geral'}
                                onChange={(e) => setEditandoTodo({
                                  ...editandoTodo,
                                  dados: { ...editandoTodo.dados, categoria: e.target.value }
                                })}
                                placeholder="Categoria (ex: trabalho, pessoal, estudo)"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <div className="form-group mb-2">
                          <label className="form-label text-sm font-weight-bold" htmlFor={`todoEditDescricao-${todo.id}`}>Descrição:</label>
                          <textarea
                            id={`todoEditDescricao-${todo.id}`}
                            className="form-control form-control-sm"
                            value={editandoTodo.dados.descricao}
                            onChange={(e) => setEditandoTodo({
                              ...editandoTodo,
                              dados: { ...editandoTodo.dados, descricao: e.target.value }
                            })}
                            placeholder="Descrição (opcional)"
                            rows={2}
                            style={{ resize: 'vertical', minHeight: '60px' }}
                          />
                        </div>
                        
                        <div className="row mb-2">
                          <div className="col-6">
                            <label className="form-label text-sm font-weight-bold" htmlFor={`todoEditPrioridade-${todo.id}`}>Prioridade:</label>
                            <select
                              id={`todoEditPrioridade-${todo.id}`}
                              className="form-control form-control-sm"
                              value={editandoTodo.dados.prioridade}
                              onChange={(e) => setEditandoTodo({
                                ...editandoTodo,
                                dados: { ...editandoTodo.dados, prioridade: e.target.value as 'baixa' | 'media' | 'alta' }
                              })}
                            >
                              <option value="baixa">Baixa</option>
                              <option value="media">Média</option>
                              <option value="alta">Alta</option>
                            </select>
                          </div>
                          <div className="col-6">
                            <label className="form-label text-sm font-weight-bold" htmlFor={`todoEditVencimento-${todo.id}`}>Vencimento:</label>
                            <input
                              id={`todoEditVencimento-${todo.id}`}
                              type="date"
                              className="form-control form-control-sm"
                              value={editandoTodo.dados.data_vencimento}
                              onChange={(e) => setEditandoTodo({
                                ...editandoTodo,
                                dados: { ...editandoTodo.dados, data_vencimento: e.target.value }
                              })}
                            />
                          </div>
                        </div>
                        
                        <div className="btn-group">
                          <button 
                            className="btn btn-success btn-sm"
                            onClick={salvarEdicaoTodo}
                            title="Salvar"
                          >
                            <i className="fas fa-save mr-1"></i>
                            Salvar
                          </button>
                          <button 
                            className="btn btn-secondary btn-sm"
                            onClick={cancelarEdicaoTodo}
                            title="Cancelar"
                          >
                            <i className="fas fa-times mr-1"></i>
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Modo de visualização
                      <div className="d-flex align-items-start">
                        {/* Handle para arrastar */}
                        <span className="handle mr-3" style={{ marginTop: '2px' }}>
                          <i className="fas fa-ellipsis-v"></i>
                          <i className="fas fa-ellipsis-v"></i>
                        </span>
                        
                        {/* Checkbox */}
                        <div className="icheck-primary d-inline mr-3" style={{ marginTop: '2px' }}>
                          <input 
                            type="checkbox" 
                            value="" 
                            name={`todo${todo.id}`}
                            id={`todoCheck${todo.id}`}
                            checked={todo.status === 'concluida'}
                            onChange={(e) => alterarStatusTodo(todo.id!, e.target.checked ? 'concluida' : 'pendente')}
                            disabled={!!editandoTodo}
                          />
                          <label htmlFor={`todoCheck${todo.id}`}></label>
                        </div>
                        
                        {/* Conteúdo */}
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <div 
                                className="font-weight-bold" 
                                onClick={() => iniciarEdicaoTodo(todo)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    iniciarEdicaoTodo(todo);
                                  }
                                }}
                                style={{ 
                                  cursor: 'pointer', 
                                  fontSize: '1.1em',
                                  textDecoration: todo.status === 'concluida' ? 'line-through' : 'none',
                                  color: todo.status === 'concluida' ? '#6c757d' : 'inherit'
                                }}
                                title="Clique para editar"
                                role="button"
                                tabIndex={0}
                              >
                                {todo.titulo}
                              </div>
                              
                              {todo.descricao && (
                                <div className="text-muted mt-1" style={{ fontSize: '0.9em' }}>
                                  {todo.descricao}
                                </div>
                              )}
                              
                              <div className="d-flex align-items-center mt-2">
                                <span 
                                  className={`badge badge-${todo.prioridade === 'alta' ? 'danger' : todo.prioridade === 'media' ? 'warning' : 'info'} mr-2`}
                                >
                                  {todo.prioridade}
                                </span>
                                
                                {todo.categoria && (
                                  <span className="badge badge-secondary mr-2">
                                    {todo.categoria}
                                  </span>
                                )}
                                
                                <span className={`badge badge-${
                                  todo.status === 'concluida' ? 'success' : 
                                  todo.status === 'em_andamento' ? 'primary' : 'light'
                                } mr-2`}>
                                  {todo.status === 'pendente' ? 'Pendente' : 
                                   todo.status === 'em_andamento' ? 'Em Andamento' : 'Concluída'}
                                </span>
                                
                                {todo.data_vencimento && (
                                  <small className="text-muted">
                                    <i className="fas fa-calendar mr-1"></i>
                                    {new Date(todo.data_vencimento).toLocaleDateString('pt-BR')}
                                  </small>
                                )}
                              </div>
                            </div>
                            
                            <div className="btn-group btn-group-sm ml-2">
                              <button 
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => iniciarEdicaoTodo(todo)}
                                title="Editar tarefa"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button 
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => {
                                  if (confirm('Tem certeza que deseja excluir esta tarefa?')) {
                                    excluirTodo(todo.id!);
                                  }
                                }}
                                title="Excluir tarefa"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
                {todosFiltrados.length === 0 && (
                  <li className="text-center text-muted py-5">
                    <i className="fas fa-tasks fa-3x mb-3"></i>
                    <h5>Nenhuma tarefa encontrada</h5>
                    <p>
                      {todos.length === 0 
                        ? 'Comece criando sua primeira tarefa!'
                        : 'Tente ajustar os filtros acima.'
                      }
                    </p>
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
